import React from "react";
import { motion } from "framer-motion";

/**
 * PageTransition - компонент для плавных переходов между страницами.
 *
 * variant="drop" (по умолчанию) - «капля воды»: страница появляется
 * увеличенной (зум 120%) и пружинно оседает до 100%; уходящая страница
 * в это время мягко тает под ней, без пустого экрана между состояниями.
 *
 * variant="slide" - страница выезжает справа налево (/profilemenu),
 * а страница под ней уезжает влево с лёгким сжатием (параллакс).
 *
 * custom = { enteringProfileMenu, leavingProfileMenu } - контекст навигации.
 * AnimatePresence прокидывает его уходящим страницам, чтобы их exit-анимация
 * соответствовала направлению перехода.
 */

const EASE_SLIDE = [0.32, 0.72, 0, 1];
const EASE_OUT = [0.4, 0, 1, 1];
const EASE_SOFT = [0.33, 1, 0.68, 1];

// пружина «падения капли»: быстрый старт, мягкое приземление
const SPRING = { type: "spring", stiffness: 200, damping: 26 };
const MENU_SHADOW = "-10px 0 36px rgba(0, 0, 0, 0.55)";

const PageTransition = ({ children, variant = "drop", custom, className = "" }) => {
  const isSlide = variant === "slide";

  const variants = {
    initial: (c) =>
      isSlide
        ? { x: "100%", boxShadow: MENU_SHADOW, zIndex: 2 }
        : c?.leavingProfileMenu
          ? { opacity: 0, zIndex: 1 } // страница под закрывающимся меню просто проявляется
          : { opacity: 0, scale: 1.2, y: 14, zIndex: 2 },
    in: (c) =>
      isSlide
        ? {
            x: 0,
            boxShadow: MENU_SHADOW,
            zIndex: 2,
            transition: { duration: 0.4, ease: EASE_SLIDE },
          }
        : {
            opacity: 1,
            scale: 1,
            y: 0,
            // под закрывающимся меню остаёмся на нижнем слое,
            // чтобы меню уезжало поверх страницы
            zIndex: c?.leavingProfileMenu ? 1 : 2,
            transition: { ...SPRING, opacity: { duration: 0.25, ease: EASE_SOFT } },
          },
    exit: (c) => {
      // само меню уезжает обратно вправо поверх новой страницы
      if (isSlide) {
        return {
          x: "100%",
          boxShadow: MENU_SHADOW,
          zIndex: 2,
          pointerEvents: "none",
          transition: { duration: 0.15, ease: EASE_OUT },
        };
      }
      // обычная страница уступает место выезжающему меню - параллакс влево
      if (c?.enteringProfileMenu) {
        return {
          x: "-32%",
          scale: 0.94,
          opacity: 0.7,
          zIndex: 1,
          pointerEvents: "none",
          transition: { duration: 0.4, ease: EASE_SLIDE },
        };
      }
      // обычный переход: старая страница мягко тает под «каплей»
      return {
        opacity: 0,
        zIndex: 1,
        pointerEvents: "none",
        transition: { duration: 0.28, ease: "easeOut" },
      };
    },
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="exit"
      variants={variants}
      custom={custom}
      className={`absolute inset-0 w-full h-full overflow-auto flex flex-col ${className}`}
      style={{ transformOrigin: "center center" }}
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;
